// src/components/programs/ProgramForm/Step2Quotation.jsx

import React, { useMemo } from "react";
import { Box, Grid, Paper, Typography, Stack, alpha, useTheme } from "@mui/material";
import useFormLanguage from "../../../hooks/forms/useFormLanguage";
import DayQuotationList from "./Step2Sections/DayQuotationList";
import GlobalDiscountSection from "./Step2Sections/GlobalDiscountSection";
import QuotationSummary from "./Step2Sections/QuotationSummary";
import { calculateProgramTotals } from "./helpers/quotationCalculations";

const Step2Quotation = ({
    formData,
    setFormData,
    errors = {},
}) => {
    const { isArabic, activeLang } = useFormLanguage();
    const theme = useTheme();

    const totals = useMemo(() => {
        return calculateProgramTotals(formData);
    }, [formData]);

    const handleUpdateProductPricing = (locations, field, value) => {
        // ✅ التحقق البسيط: رفض فقط undefined
        if (value === undefined) {
            console.log('Invalid value:', value);
            return;
        }

        // Preserve null as a valid deletion marker for numeric pricing fields
        let numValue = value;

        // إذا كنت تريد التأكد من الأرقام للحقول الرقمية فقط:
        if (field === 'pricing.discount_value') {
            numValue = Number(value);
            if (isNaN(numValue)) return;
        }
        // أما إذا كان pricing.discount_type فنتركه نصاً كما هو
        if (!locations || !Array.isArray(locations) || locations.length === 0) return;

        const newItinerary = [...formData.itinerary];

        locations.forEach(({ dayIndex, productIndex }) => {
            if (dayIndex < 0 || dayIndex >= newItinerary.length) return;
            const day = newItinerary[dayIndex];
            if (!day || !day.products || !Array.isArray(day.products)) return;
            if (productIndex < 0 || productIndex >= day.products.length) return;

            const product = { ...day.products[productIndex] };
            const keys = field.split(".");
            let current = product;

            for (let i = 0; i < keys.length - 1; i++) {
                if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                    current[keys[i]] = {};
                }
                current[keys[i]] = { ...current[keys[i]] };
                current = current[keys[i]];
            }

            // ✅ حفظ القيمة الرقمية (بما فيها 0)
            current[keys[keys.length - 1]] = numValue;
            day.products[productIndex] = product;
        });

        // ✅ تحديث الـ state
        setFormData({ ...formData, itinerary: newItinerary });
    };

    const handleUpdateHotelPricing = (hotelIndex, field, value) => {
        if (value === undefined) return;
        const newHotels = [...formData.hotels];
        if (hotelIndex < 0 || hotelIndex >= newHotels.length) return;

        const hotel = { ...newHotels[hotelIndex] };
        const keys = field.split(".");
        let current = hotel;

        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                current[keys[i]] = {};
            }
            current[keys[i]] = { ...current[keys[i]] };
            current = current[keys[i]];
        }

        current[keys[keys.length - 1]] = value;
        newHotels[hotelIndex] = hotel;
        setFormData({ ...formData, hotels: newHotels });
    };

    const handleGlobalDiscountChange = (field, value) => {
        setFormData({
            ...formData,
            global_discount: {
                ...formData.global_discount,
                [field]: value,
            },
        });
    };

    return (
        <>
            {/* Header */}
            <Box sx={{ mb: 2.5, pb: 2, borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: '1.1rem' }}>
                    {isArabic ? '💰 التسعير والعروض' : '💰 Quotation & Pricing'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    {isArabic
                        ? 'قم بتعديل الأسعار والخصومات لكل منتج'
                        : 'Adjust prices and discounts for each product'}
                </Typography>
            </Box>

            {/* Grid Layout */}
            <Grid container spacing={2.5} sx={{ pb: 4 }}>
                {/* Left Side */}
                <Grid item xs={12} lg={7}>
                    <Paper
                        elevation={2}
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            position: 'sticky',
                            top: 20,
                        }}
                    >
                        <Box
                            sx={{
                                p: 1.5,
                                bgcolor: 'primary.main',
                                color: 'white',
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                            }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
                                {isArabic ? '📋 المنتجات والأسعار' : '📋 Products & Prices'}
                            </Typography>
                        </Box>

                        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                            <DayQuotationList
                                formData={formData}
                                globalDiscount={formData.global_discount}
                                onUpdateProductPricing={handleUpdateProductPricing}
                                onUpdateHotelPricing={handleUpdateHotelPricing}
                                errors={errors}
                                isArabic={isArabic}
                                activeLang={activeLang}
                            />
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Side - STICKY */}
                <Grid item xs={12} lg={5}>
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 20,
                        }}
                    >
                        <Stack spacing={2}>
                            {/* Global Discount */}
                            <Paper
                                elevation={2}
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        p: 1.5,
                                        bgcolor: 'warning.main',
                                        color: 'white',
                                        borderTopLeftRadius: 8,
                                        borderTopRightRadius: 8,
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
                                        {isArabic ? '🎁 الخصم الشامل' : '🎁 Global Discount'}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2 }}>
                                    <GlobalDiscountSection
                                        globalDiscount={formData.global_discount}
                                        onDiscountChange={handleGlobalDiscountChange}
                                        errors={errors}
                                        isArabic={isArabic}
                                    />
                                </Box>
                            </Paper>

                            {/* Summary */}
                            <Paper
                                elevation={2}
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        p: 1.5,
                                        bgcolor: 'success.main',
                                        color: 'white',
                                        borderTopLeftRadius: 8,
                                        borderTopRightRadius: 8,
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
                                        {isArabic ? '📊 الملخص المالي' : '📊 Financial Summary'}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2 }}>
                                    <QuotationSummary
                                        globalDiscount={formData.global_discount}
                                        totals={totals}
                                        formData={formData}
                                        isArabic={isArabic}
                                    />
                                </Box>
                            </Paper>
                        </Stack>
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default Step2Quotation;
